import {
  CsvExportParams,
  ExcelExportParams,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
} from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import { useCallback } from 'react';
import { trackEvent } from '../../util/datadogTracking';

/**
 * Grid power-user RUM instrumentation (FD-3122), wired once at the shared grid
 * wrappers (AgTable, AgTableWithBaseStyle) so every grid surface emits these
 * events without per-page duplication.
 *
 * Privacy: emit only counts/format/presence — never filter values or
 * entity-identifying data — consistent with the RUM masking posture.
 */

// Guards so re-entrant onGridReady (grid re-init) can't double-wrap exports or
// re-emit unchanged filters. Keyed by GridApi; entries die with the grid.
const instrumentedApis = new WeakSet<GridApi>();
const lastFilterSignature = new WeakMap<GridApi, string>();

/**
 * Count leaf (non-group) rows after filter/sort — the rows actually written to
 * an export. `getDisplayedRowCount()` counts currently-rendered rows, which
 * under-counts when groups are collapsed, so it is wrong for grouped grids.
 */
function countLeafRows(api: GridApi): number {
  let count = 0;
  api.forEachNodeAfterFilterAndSort((node: IRowNode) => {
    if (!node.group) {count += 1;}
  });
  return count;
}

/**
 * Wrap a grid's CSV/Excel export methods so each export emits `grid.exported`
 * with `{ format, row_count }` then delegates to the original method. ag-grid
 * exposes no export event, and exports are triggered per-page via `gridApi`,
 * so wrapping the api is the only true "once at the shared layer" hook.
 * Idempotent: a second call for the same api is a no-op.
 */
export function instrumentGridExport(api: GridApi): void {
  if (instrumentedApis.has(api)) {return;}
  instrumentedApis.add(api);

  const originalCsv = api.exportDataAsCsv.bind(api);
  api.exportDataAsCsv = (params?: CsvExportParams) => {
    trackEvent('grid.exported', { format: 'csv', row_count: countLeafRows(api) });
    return originalCsv(params);
  };

  const originalExcel = api.exportDataAsExcel.bind(api);
  api.exportDataAsExcel = (params?: ExcelExportParams) => {
    trackEvent('grid.exported', { format: 'excel', row_count: countLeafRows(api) });
    return originalExcel(params);
  };
}

/**
 * Emit `grid.filter_applied` from an ag-grid `FilterChangedEvent`, carrying the
 * number of active column filters and whether a quick-filter/search is present.
 * Value-free by design.
 *
 * Gated on a change in the filter state: callers invoke `api.onFilterChanged()`
 * imperatively for data-sync (and quick-filter typing fires per keystroke), so
 * emitting on every event would over-count actions the user never took. The
 * signature is compared in-memory only and never leaves the browser.
 */
// Order-stable serialization: ag-grid can rebuild the filter model (and its
// nested objects) with different key order on a grid re-init; sorting keys at
// every level keys the signature to logical state, not insertion order.
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key: string, val: unknown) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const obj = val as Record<string, unknown>;
      return Object.fromEntries(
        Object.keys(obj)
          .sort()
          .map((k) => [k, obj[k]])
      );
    }
    return val;
  });
}

export function trackGridFilterApplied(event: FilterChangedEvent): void {
  const { api } = event;
  const filterModel = api.getFilterModel() ?? {};
  const filterCount = Object.keys(filterModel).length;
  const hasSearch = api.isQuickFilterPresent();
  const signature = `${stableStringify(filterModel)}|${hasSearch}`;

  const prior = lastFilterSignature.get(api);
  lastFilterSignature.set(api, signature);

  if (prior === signature) {return;} // unchanged (programmatic refresh / repeat keystroke)
  if (prior === undefined && filterCount === 0 && !hasSearch) {return;} // initial empty state

  trackEvent('grid.filter_applied', { filter_count: filterCount, has_search: hasSearch });
}

/**
 * Compose RUM instrumentation onto a grid wrapper's `onGridReady` /
 * `onFilterChanged`, forwarding to the caller's handler (whether supplied as a
 * top-level prop or via `gridOptions`). Returned handlers must be passed as
 * top-level `<AgGridReact>` props: ag-grid lets a top-level prop win over its
 * `gridOptions` counterpart, so a `gridOptions`-only hook would be overridden
 * and silently never run.
 */
export function useGridTrackingHandlers(props: AgGridReactProps) {
  const callerOnGridReady = props.onGridReady ?? props.gridOptions?.onGridReady;
  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      instrumentGridExport(params.api);
      callerOnGridReady?.(params);
    },
    [callerOnGridReady]
  );

  const callerOnFilterChanged = props.onFilterChanged ?? props.gridOptions?.onFilterChanged;
  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      trackGridFilterApplied(event);
      callerOnFilterChanged?.(event);
    },
    [callerOnFilterChanged]
  );

  return { onGridReady, onFilterChanged };
}
